import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { StoreBlock } from '@/api/dashboard'
import { useStoresQuery } from '@/layouts/store/api'
import { useGenerateCompanyPath } from '@/libs'
import { useState } from 'react'
import { FormErrorMessage, Select } from '@dashboard/ui'
import { useQuery } from '@tanstack/react-query'
import { itemCategoriesQuery } from '@/layouts/item-category'
import { DEFAULT_LOAD_LIMIT } from '@/api/useGameItems'
import { FieldGroup } from '@/ui'
import { ErrorMessage } from '@hookform/error-message'

enum StoreBlockType {
  ALL = 'all',
  STORE = 'store',
  CATEGORY = 'category',
}

export const StoreOrSlugItemsForm = () => {
  const { t } = useTranslation()

  const { companyId, gameId } = useGenerateCompanyPath()
  const { data: stores = [] } = useStoresQuery(companyId, gameId, { limit: 100 })
  const { data: categories = [] } = useQuery({
    ...itemCategoriesQuery(companyId, gameId, { limit: DEFAULT_LOAD_LIMIT }),
  })

  const {
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<StoreBlock>()

  const [storeId, category_id] = watch(['store_id', 'category_id'])
  const [storeBlockType, setStoreBlockType] = useState<StoreBlockType>(
    storeId ? StoreBlockType.STORE : category_id ? StoreBlockType.CATEGORY : StoreBlockType.ALL,
  )

  return (
    <div>
      <FieldGroup label={t('hub-editor.store-block-type')}>
        <Select
          value={storeBlockType}
          items={[
            {
              value: StoreBlockType.ALL,
              children: t('hub-editor.store-block-type.all'),
              caption: t('hub-editor.store-block-type.all.desc'),
            },
            {
              value: StoreBlockType.STORE,
              children: t('hub-editor.store-block-type.specified'),
              caption: t('hub-editor.store-block-type.specified.desc'),
            },
            {
              value: StoreBlockType.CATEGORY,
              children: t('sku.item.category'),
              caption: t('hub-editor.store-block-type.category.desc'),
            },
          ]}
          onChange={v => {
            setStoreBlockType(v as StoreBlockType)
            if (v != StoreBlockType.CATEGORY) {
              setValue('category_id', null as unknown as string, { shouldDirty: true })
            }
            if (v != StoreBlockType.STORE) {
              setValue('store_id', null as unknown as string, { shouldDirty: true })
            }
          }}
        />
      </FieldGroup>

      {storeBlockType == StoreBlockType.STORE && (
        <FieldGroup label={t('store.store')}>
          <Controller
            rules={{ required: t('validation.required') }}
            control={control}
            name="store_id"
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={v => field.onChange(v)}
                items={stores.map(s => ({
                  value: s.id,
                  children: s.name,
                }))}
              />
            )}
          />
          <ErrorMessage
            name="store_id"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>
      )}

      {storeBlockType == StoreBlockType.CATEGORY && (
        <FieldGroup label={t('hub-editor.block-category')}>
          <Controller
            rules={{ required: t('validation.required') }}
            control={control}
            name="category_id"
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={v => field.onChange(v)}
                items={categories.map(s => ({
                  value: s.id,
                  children: s.name,
                }))}
              />
            )}
          />
          <ErrorMessage
            name="category_id"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>
      )}
    </div>
  )
}
